import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api, supabase } from '../../lib/api';
import { Button, getLeadTypeBadge } from '../../components/ui/Kit';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import Spinner from '../../components/ui/Spinner';
import {
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
import * as FiIcons from 'react-icons/fi';

const {
  FiMessageSquare, FiMail, FiSearch, FiSend, FiArrowLeft,
  FiExternalLink, FiHeadphones, FiMessageCircle, FiUserPlus,
} = FiIcons;

/* ─── Helpers ──────────────────────────────────────────────────────── */

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diffMs / 60000);
  const hr = Math.floor(diffMs / 3600000);
  const day = Math.floor(diffMs / 86400000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMessageTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date}, ${time}`;
}

function formatDateSeparator(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function getInitials(first, last) {
  return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || '?';
}

function leadName(conv) {
  if (conv.lead_name) return conv.lead_name;
  const l = conv.lead;
  if (l?.first_name || l?.last_name) return [l.first_name, l.last_name].filter(Boolean).join(' ');
  return conv.from_number || conv.to_number || 'Unknown';
}

function leadPhone(conv) {
  return conv.lead_phone || conv.lead?.phone || conv.from_number || conv.to_number || '';
}

/** External party phone for unknown (no lead_id) conversations */
function contactPhone(conv) {
  if (conv.lead?.phone) return conv.lead.phone;
  if (conv.lead_phone) return conv.lead_phone;
  if (conv.lead_id) return leadPhone(conv);
  if (conv.direction === 'inbound') return conv.from_number;
  if (conv.direction === 'outbound') return conv.to_number;
  return conv.from_number || conv.to_number || '';
}

function phonesMatchLoose(a, b) {
  const digits = (p) => String(p || '').replace(/\D/g, '');
  const norm = (d) => {
    if (!d) return '';
    if (d.length === 10) return d;
    if (d.length === 11 && d.startsWith('1')) return d.slice(1);
    return d.length > 11 ? d.slice(-10) : d;
  };
  const na = norm(digits(a));
  const nb = norm(digits(b));
  return Boolean(na && nb && na === nb);
}

function conversationMatches(conv, leadId, phone) {
  if (leadId) return conv.lead_id === leadId;
  if (!phone || conv.lead_id) return false;
  return phonesMatchLoose(contactPhone(conv), phone);
}

const SMS_CHAR_LIMIT = 160;

function plainTextToHtml(text) {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p style="font-family:Roboto,Arial,sans-serif;font-size:15px;line-height:1.6;color:#1B4C87;">${escaped.replace(/\n/g, '<br />')}</p>`;
}

function htmlToPlainText(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function emailLeadName(conv) {
  const l = conv.lead;
  if (l?.first_name || l?.last_name) return [l.first_name, l.last_name].filter(Boolean).join(' ');
  return 'Unknown lead';
}

function emailLeadAddress(conv) {
  return conv.to_email || conv.lead?.email || '';
}

function agentInitials(user) {
  const name = user?.full_name || user?.email || '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

/* ─── Email tab (email-native two-panel layout) ───────────────────── */

function EmailInboxTab({
  addToast,
  navigate,
  activeLeadId,
  setSearchParams,
  mobileShowThread,
  setMobileShowThread,
}) {
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [emails, setEmails] = useState([]);
  const [activeLead, setActiveLead] = useState(null);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.emailLogs.getConversations();
        if (!cancelled) setConversations(data || []);
      } catch (err) {
        console.error('Failed to load email conversations:', err);
      } finally {
        if (!cancelled) setLoadingConversations(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!activeLeadId) {
      setEmails([]);
      setActiveLead(null);
      return;
    }
    let cancelled = false;
    setLoadingEmails(true);
    (async () => {
      try {
        const [logs, lead] = await Promise.all([
          api.emailLogs.getByLead(activeLeadId),
          api.leads.getById(activeLeadId).catch(() => null),
        ]);
        if (!cancelled) {
          setEmails(Array.isArray(logs) ? logs : []);
          setActiveLead(lead);
        }
      } catch (err) {
        console.error('Failed to load emails:', err);
      } finally {
        if (!cancelled) setLoadingEmails(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeLeadId]);

  const selectLead = useCallback((conv) => {
    if (conv.lead_id) {
      setSearchParams({ lead: conv.lead_id });
      setMobileShowThread(true);
    }
  }, [setSearchParams, setMobileShowThread]);

  const goBackToList = useCallback(() => {
    setSearchParams({});
    setMobileShowThread(false);
  }, [setSearchParams, setMobileShowThread]);

  const refreshConversations = useCallback(async () => {
    try {
      const data = await api.emailLogs.getConversations();
      setConversations(data || []);
    } catch (err) {
      console.error('Failed to refresh email conversations:', err);
    }
  }, []);

  const handleSendEmail = useCallback(async () => {
    const subject = emailSubject.trim();
    const body = emailBody.trim();
    const toEmail = activeLead?.email?.trim();
    if (!subject || !body || !activeLeadId || !toEmail || sendingEmail) return;

    setSendingEmail(true);
    try {
      const toName = [activeLead.first_name, activeLead.last_name].filter(Boolean).join(' ') || toEmail;
      const html_body = plainTextToHtml(body);
      const result = await api.email.send({
        to_email: toEmail,
        to_name: toName,
        subject,
        html_body,
      });
      await api.emailLogs.create({
        lead_id: activeLeadId,
        to_email: toEmail,
        subject,
        body_html: html_body,
        brevo_message_id: result.messageId,
      });
      const logs = await api.emailLogs.getByLead(activeLeadId);
      setEmails(Array.isArray(logs) ? logs : []);
      await refreshConversations();
      setEmailSubject('');
      setEmailBody('');
      addToast({ type: 'success', title: 'Email sent' });
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to send email', message: err.message });
    } finally {
      setSendingEmail(false);
    }
  }, [
    emailSubject,
    emailBody,
    activeLeadId,
    activeLead,
    sendingEmail,
    addToast,
    refreshConversations,
  ]);

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter((c) => {
      const name = emailLeadName(c).toLowerCase();
      const addr = emailLeadAddress(c).toLowerCase();
      const subj = (c.subject || '').toLowerCase();
      return name.includes(q) || addr.includes(q) || subj.includes(q);
    });
  }, [conversations, searchQuery]);

  const activeConversation = useMemo(
    () => conversations.find((c) => c.lead_id === activeLeadId),
    [conversations, activeLeadId],
  );

  const headerName = activeLead
    ? [activeLead.first_name, activeLead.last_name].filter(Boolean).join(' ')
    : emailLeadName(activeConversation || {});
  const headerEmail = activeLead?.email?.trim() || emailLeadAddress(activeConversation || {});

  return (
    <div className="flex-1 flex min-h-0 overflow-hidden">
      {/* Left: leads with email history */}
      <div
        className={`w-full md:w-80 md:min-w-[320px] md:max-w-[320px] border-r border-gray-200 flex flex-col bg-white ${
          mobileShowThread ? 'hidden md:flex' : 'flex'
        }`}
      >
        <div className="px-4 py-3 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Sent emails</h2>
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, email, subject…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loadingConversations ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="md" className="text-primary" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <SafeIcon icon={FiMail} className="h-7 w-7 text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                {searchQuery ? 'No results' : 'No emails sent yet'}
              </h3>
              <p className="text-xs text-gray-500">
                {searchQuery
                  ? 'Try a different search term'
                  : 'Emails you send from lead pages will appear here'}
              </p>
            </div>
          ) : (
            filteredConversations.map((conv) => {
              const name = emailLeadName(conv);
              const addr = emailLeadAddress(conv);
              const isActive = conv.lead_id === activeLeadId;
              const initials = getInitials(conv.lead?.first_name, conv.lead?.last_name);
              const agentName = conv.agent?.full_name || conv.agent?.email || 'Unknown';

              return (
                <button
                  key={conv.lead_id}
                  type="button"
                  onClick={() => selectLead(conv)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-gray-50 ${
                    isActive
                      ? 'bg-[#00AAFF]/10 border-l-2 border-l-[#00AAFF]'
                      : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-xs font-bold text-white shrink-0">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${isActive ? 'font-semibold text-[#00AAFF]' : 'font-medium text-gray-900'}`}>
                        {name}
                      </span>
                      <span className="text-[11px] text-gray-400 whitespace-nowrap shrink-0">
                        {relativeTime(conv.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{addr}</p>
                    <p className="text-xs text-gray-600 mt-1 truncate font-medium">{conv.subject || '(No subject)'}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5 truncate">Sent by {agentName}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Right: thread + compose */}
      <div
        className={`flex-1 flex flex-col min-w-0 bg-white ${
          !mobileShowThread ? 'hidden md:flex' : 'flex'
        }`}
      >
        {!activeLeadId ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMail} className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a lead</h3>
              <p className="text-sm text-gray-500">Choose a lead from the left to view sent emails</p>
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 md:px-6 py-3 border-b border-gray-200 flex items-center gap-3 shrink-0 bg-white">
              <button
                type="button"
                onClick={goBackToList}
                className="md:hidden p-1.5 -ml-1 rounded-md text-gray-500 hover:bg-gray-100"
                aria-label="Back to lead list"
              >
                <SafeIcon icon={FiArrowLeft} className="h-5 w-5" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold text-gray-900 truncate">{headerName}</h2>
                  {activeLead?.lead_type && getLeadTypeBadge(activeLead.lead_type)}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{headerEmail}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/leads/${activeLeadId}`)}
                className="hidden sm:inline-flex shrink-0"
              >
                <SafeIcon icon={FiExternalLink} className="h-3.5 w-3.5 mr-1.5" />
                View Lead
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 bg-gray-50/80 overscroll-contain">
              {loadingEmails ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="md" className="text-primary" />
                </div>
              ) : emails.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No emails for this lead yet</p>
              ) : (
                <div className="space-y-3 max-w-3xl mx-auto">
                  {emails.map((log) => {
                    const agentName = log.user?.full_name || log.user?.email || 'Unknown';
                    const bodyText = htmlToPlainText(log.body_html);
                    return (
                      <article
                        key={log.id}
                        className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:p-5"
                      >
                        <h3 className="text-base font-bold text-gray-900 mb-2">{log.subject}</h3>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed mb-4">
                          {bodyText}
                        </p>
                        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                          <div className="w-8 h-8 rounded-full bg-secondary text-white flex items-center justify-center text-xs font-bold shrink-0">
                            {agentInitials(log.user)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-900">{agentName}</p>
                            <p className="text-[11px] text-gray-400">{formatMessageTime(log.created_at)}</p>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-gray-200 bg-white px-4 md:px-6 py-4">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">New Email</h3>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                {!headerEmail ? (
                  <p className="text-sm text-gray-500 text-center py-2">
                    This lead has no email address. Add one on the lead profile to send mail.
                  </p>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Subject"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      disabled={sendingEmail}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-2"
                    />
                    <textarea
                      rows={4}
                      placeholder="Write your message…"
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      disabled={sendingEmail}
                      className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y min-h-[96px] mb-3"
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
                        onClick={handleSendEmail}
                      >
                        {sendingEmail ? (
                          <>
                            <Spinner className="mr-2" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiSend} className="h-4 w-4 mr-1.5" />
                            Send Email
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Component ────────────────────────────────────────────────────── */

export default function Inbox() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState('sms');

  // Conversation list
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Active thread
  const activeLeadId = searchParams.get('lead');
  const activePhone = searchParams.get('phone');
  const hasActiveThread = !!(activeLeadId || activePhone);
  const [thread, setThread] = useState([]);
  const [activeLead, setActiveLead] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Mobile: show thread or list
  const [mobileShowThread, setMobileShowThread] = useState(hasActiveThread);

  const { users: directoryUsers } = useUsersDirectory();
  const agentMap = useMemo(() => {
    const m = {};
    for (const u of directoryUsers || []) {
      if (u?.id) m[u.id] = u.full_name || null;
    }
    return m;
  }, [directoryUsers]);

  const threadEndRef = useRef(null);
  const textareaRef = useRef(null);
  const activeLeadIdRef = useRef(activeLeadId);
  const activePhoneRef = useRef(activePhone);
  activeLeadIdRef.current = activeLeadId;
  activePhoneRef.current = activePhone;

  const scrollToBottom = useCallback(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  /* ── Fetch conversations ── */
  // sms_messages RLS (sms_messages_select_visibility) filters rows to leads the
  // current user can access — same rules as the Leads list. No client-side
  // role filtering needed here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.smsMessages.getConversations();
        if (!cancelled) setConversations(data || []);
      } catch (err) {
        console.error('Failed to load conversations:', err);
      } finally {
        if (!cancelled) setLoadingConversations(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ── Load thread when selection changes ── */
  useEffect(() => {
    if (!activeLeadId && !activePhone) {
      setThread([]);
      setActiveLead(null);
      return;
    }
    let cancelled = false;
    setLoadingThread(true);

    (async () => {
      try {
        if (activeLeadId) {
          const [msgs, lead] = await Promise.all([
            api.smsMessages.getThread(activeLeadId),
            api.leads.getById(activeLeadId).catch(() => null),
          ]);
          if (!cancelled) {
            setThread(msgs || []);
            setActiveLead(lead);
          }
        } else {
          const msgs = await api.smsMessages.getThreadByPhone(activePhone);
          if (!cancelled) {
            setThread(msgs || []);
            setActiveLead(null);
          }
        }
      } catch (err) {
        console.error('Failed to load thread:', err);
      } finally {
        if (!cancelled) setLoadingThread(false);
      }
    })();

    const markRead = activeLeadId
      ? api.smsMessages.markThreadRead(activeLeadId)
      : api.smsMessages.markThreadReadByPhone(activePhone);

    markRead
      .then(() => {
        setConversations(prev =>
          prev.map(c =>
            conversationMatches(c, activeLeadId, activePhone)
              ? { ...c, unread_count: 0 }
              : c,
          ),
        );
        window.dispatchEvent(new CustomEvent('sms-unread-change'));
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [activeLeadId, activePhone]);

  /* ── Auto-scroll on new messages ── */
  useEffect(() => {
    if (thread.length) setTimeout(scrollToBottom, 80);
  }, [thread.length, scrollToBottom]);

  /* ── Realtime: new messages ── */
  useEffect(() => {
    if (!supabase || !user?.agency_id) return undefined;

    const channel = supabase
      .channel('inbox-live')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'sms_messages',
        filter: `agency_id=eq.${user.agency_id}`,
      }, (payload) => {
        const msg = payload.new;
        if (!msg) return;

        // Update conversation list
        setConversations(prev => {
          const idx = prev.findIndex(c =>
            msg.lead_id
              ? c.lead_id === msg.lead_id
              : !c.lead_id && phonesMatchLoose(c.from_number, msg.from_number),
          );
          if (idx >= 0) {
            const updated = [...prev];
            const conv = { ...updated[idx], body: msg.body, created_at: msg.created_at, direction: msg.direction };
            const isActive = msg.lead_id
              ? msg.lead_id === activeLeadIdRef.current
              : !activePhoneRef.current
                ? false
                : phonesMatchLoose(msg.from_number, activePhoneRef.current)
                  || phonesMatchLoose(msg.to_number, activePhoneRef.current);
            if (msg.direction === 'inbound' && !isActive) {
              conv.unread_count = (conv.unread_count || 0) + 1;
            }
            updated.splice(idx, 1);
            updated.unshift(conv);
            return updated;
          }
          return [{ ...msg, unread_count: msg.direction === 'inbound' ? 1 : 0 }, ...prev];
        });

        // Append to active thread
        const isActiveThread = msg.lead_id
          ? msg.lead_id === activeLeadIdRef.current
          : activePhoneRef.current
            && (phonesMatchLoose(msg.from_number, activePhoneRef.current)
              || phonesMatchLoose(msg.to_number, activePhoneRef.current));

        if (isActiveThread) {
          setThread(prev => {
            // Skip if Realtime replays an INSERT we already have (e.g. edge fn + client both persisted).
            if (prev.some(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });

          if (msg.direction === 'inbound') {
            const markRead = activeLeadIdRef.current
              ? api.smsMessages.markThreadRead(activeLeadIdRef.current)
              : api.smsMessages.markThreadReadByPhone(activePhoneRef.current);
            markRead
              .then(() => {
                window.dispatchEvent(new Event('sms-unread-change'));
              })
              .catch(() => {});
          }
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.agency_id]);

  /* ── Select / deselect conversation ── */
  const selectConversation = useCallback((conv) => {
    if (conv.lead_id) {
      setSearchParams({ lead: conv.lead_id });
    } else {
      const phone = contactPhone(conv);
      if (phone) setSearchParams({ phone });
    }
    setMobileShowThread(true);
  }, [setSearchParams]);

  const goBackToList = useCallback(() => {
    setSearchParams({});
    setMobileShowThread(false);
  }, [setSearchParams]);

  /* ── Send message ── */
  const handleSend = useCallback(async () => {
    if (!newMessage.trim() || sendingMessage || (!activeLeadId && !activePhone)) return;

    const body = newMessage.trim();
    const activeConv = conversations.find(c => conversationMatches(c, activeLeadId, activePhone));
    const phone = activeLead?.phone || activePhone || leadPhone(activeConv || {});
    const dialer = user?.preferred_dialer || 'twilio';

    // Realtime subscription appends sent messages — no optimistic thread update.
    setNewMessage('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setSendingMessage(true);

    try {
      const result = await api.calls.sendSms({
        to: phone,
        message: body,
        lead_id: activeLeadId || null,
        agency_id: user?.agency_id,
        preferredDialer: dialer,
      });

      // twilio-sms already inserts into sms_messages; saveOutbound only for telnyx.
      if (dialer === 'telnyx') {
        try {
          await api.smsMessages.saveOutbound({
            lead_id: activeLeadId || null,
            to_number: phone,
            from_number: result?.from || '',
            body,
            twilio_message_sid: result?.sid || null,
          });
        } catch { /* ignore duplicate persistence errors */ }
      }

      setConversations(prev => {
        const idx = prev.findIndex(c => conversationMatches(c, activeLeadId, activePhone));
        if (idx >= 0) {
          const updated = [...prev];
          updated.splice(idx, 1);
          updated.unshift({ ...prev[idx], body, created_at: new Date().toISOString(), direction: 'outbound' });
          return updated;
        }
        return prev;
      });
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to send SMS', message: err.message });
    } finally {
      setSendingMessage(false);
    }
  }, [newMessage, activeLeadId, activePhone, activeLead, conversations, user, addToast, sendingMessage]);

  /* ── Textarea auto-resize ── */
  const handleTextareaInput = useCallback((e) => {
    setNewMessage(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  /* ── Derived data ── */
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter(c => leadName(c).toLowerCase().includes(q) || leadPhone(c).includes(q));
  }, [conversations, searchQuery]);

  const totalUnread = useMemo(
    () => conversations.reduce((s, c) => s + (c.unread_count || 0), 0),
    [conversations],
  );

  const activeConversation = useMemo(
    () => conversations.find(c => conversationMatches(c, activeLeadId, activePhone)),
    [conversations, activeLeadId, activePhone],
  );

  const isUnknownContact = !activeLeadId && !!activePhone;
  const ownerDisplayName = activeLead
    ? resolveDisplayName(activeLead.created_by, activeLead.owner)
    : null;

  const groupedThread = useMemo(() => {
    const groups = [];
    let lastDate = '';
    for (const msg of thread) {
      const d = new Date(msg.created_at).toDateString();
      if (d !== lastDate) { groups.push({ _sep: true, date: msg.created_at }); lastDate = d; }
      groups.push(msg);
    }
    return groups;
  }, [thread]);

  /* ─── Render ─────────────────────────────────────────────────────── */

  return (
    <div className="flex flex-col h-full bg-white">
      {/* ─── Tab bar ─── */}
      <div className="flex items-center border-b border-gray-200 px-4 md:px-6 shrink-0">
        <h1 className="text-xl font-bold text-gray-900 mr-6 py-3">Inbox</h1>
        <div className="flex gap-1">
          {[
            { key: 'sms', label: 'SMS', icon: FiMessageSquare },
            { key: 'email', label: 'Email', icon: FiMail },
          ].map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <SafeIcon icon={tab.icon} className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Tab content ─── */}
      {activeTab === 'email' ? (
        <EmailInboxTab
          addToast={addToast}
          navigate={navigate}
          activeLeadId={activeLeadId}
          setSearchParams={setSearchParams}
          mobileShowThread={mobileShowThread}
          setMobileShowThread={setMobileShowThread}
        />
      ) : (
        /* ─── SMS two-panel layout ─── */
        <div className="flex-1 flex min-h-0 overflow-hidden">

          {/* ─── LEFT: Conversation list ─── */}
          <div
            className={`w-full md:w-80 md:min-w-[320px] md:max-w-[320px] border-r border-gray-200 flex flex-col bg-white ${
              mobileShowThread ? 'hidden md:flex' : 'flex'
            }`}
          >
            {/* Header + search */}
            <div className="px-4 py-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-base font-semibold text-gray-900">Messages</h2>
                {totalUnread > 0 && (
                  <span className="bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </div>
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {loadingConversations ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="md" className="text-primary" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                  <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <SafeIcon icon={FiMessageSquare} className="h-7 w-7 text-gray-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    {searchQuery ? 'No results' : 'No messages yet'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {searchQuery ? 'Try a different search term' : 'Messages sent to leads will appear here'}
                  </p>
                </div>
              ) : (
                filteredConversations.map(conv => {
                  const name = leadName(conv);
                  const phone = leadPhone(conv);
                  const isActive = conversationMatches(conv, activeLeadId, activePhone);
                  const hasUnread = (conv.unread_count || 0) > 0;
                  const isUnknown = !conv.lead_id;
                  const initials = isUnknown
                    ? '?'
                    : getInitials(
                        conv.lead?.first_name || name.split(' ')[0],
                        conv.lead?.last_name || name.split(' ')[1],
                      );

                  return (
                    <button
                      key={conv.lead_id || contactPhone(conv) || conv.id}
                      type="button"
                      onClick={() => selectConversation(conv)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-gray-50 ${
                        isActive
                          ? 'bg-blue-50 border-l-2 border-l-primary'
                          : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-center self-center shrink-0 w-2">
                        {hasUnread && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>

                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-xs font-bold text-white shrink-0">
                        {initials}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-900'}`}>
                            {name}
                          </span>
                          <span className="text-[11px] text-gray-400 whitespace-nowrap shrink-0">
                            {relativeTime(conv.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{phone}</p>
                        <p className={`text-xs mt-1 truncate ${hasUnread ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                          {conv.body || 'No messages'}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* ─── RIGHT: Active conversation ─── */}
          <div
            className={`flex-1 flex flex-col min-w-0 bg-white ${
              !mobileShowThread ? 'hidden md:flex' : 'flex'
            }`}
          >
            {!hasActiveThread ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiMessageCircle} className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a conversation</h3>
                  <p className="text-sm text-gray-500">Choose a conversation from the left to view messages</p>
                </div>
              </div>
            ) : (
              <>
                {/* ── Header ── */}
                <div className="px-4 md:px-6 py-3 border-b border-gray-200 flex items-center gap-3 shrink-0 bg-white">
                  <button
                    type="button"
                    onClick={goBackToList}
                    className="md:hidden p-1.5 -ml-1 rounded-md text-gray-500 hover:bg-gray-100"
                    aria-label="Back to conversations"
                  >
                    <SafeIcon icon={FiArrowLeft} className="h-5 w-5" />
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-bold text-gray-900 truncate">
                        {isUnknownContact
                          ? 'Unknown Contact'
                          : activeLead
                            ? [activeLead.first_name, activeLead.last_name].filter(Boolean).join(' ')
                            : leadName(activeConversation || {})}
                      </h2>
                      {isUnknownContact && (
                        <span className="text-[10px] font-medium uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                          Unknown
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {activeLead?.phone || activePhone || leadPhone(activeConversation || {})}
                    </p>
                    {!isUnknownContact && activeLead && (activeLead.lead_type || activeLead.created_by) && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                        {activeLead.lead_type && getLeadTypeBadge(activeLead.lead_type)}
                        {ownerDisplayName && (
                          <span className="text-xs text-gray-500">
                            Owner: {ownerDisplayName}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {isUnknownContact ? (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => navigate(`/leads/new?phone=${encodeURIComponent(activePhone)}`)}
                        className="inline-flex"
                      >
                        <SafeIcon icon={FiUserPlus} className="h-3.5 w-3.5 mr-1.5" />
                        Create Lead from this number
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/leads/${activeLeadId}`)}
                          className="hidden sm:inline-flex"
                        >
                          <SafeIcon icon={FiExternalLink} className="h-3.5 w-3.5 mr-1.5" />
                          View Lead
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/workspace?lead=${activeLeadId}`)}
                          className="hidden sm:inline-flex"
                        >
                          <SafeIcon icon={FiHeadphones} className="h-3.5 w-3.5 mr-1.5" />
                          Open Workspace
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* ── Thread ── */}
                <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 bg-gray-50 overscroll-contain">
                  {loadingThread ? (
                    <div className="flex items-center justify-center h-full">
                      <Spinner size="md" className="text-primary" />
                    </div>
                  ) : thread.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-sm text-gray-400">
                      No messages in this conversation yet
                    </div>
                  ) : (
                    <div className="space-y-1 max-w-2xl mx-auto">
                      {groupedThread.map((item, i) => {
                        if (item._sep) {
                          return (
                            <div key={`sep-${i}`} className="flex items-center justify-center py-3">
                              <span className="text-[11px] text-gray-400 bg-gray-100 px-3 py-0.5 rounded-full font-medium">
                                {formatDateSeparator(item.date)}
                              </span>
                            </div>
                          );
                        }

                        const isOut = item.direction === 'outbound';
                        const agentName = isOut && item.agent_id ? agentMap[item.agent_id] : null;

                        return (
                          <div key={item.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] flex flex-col ${isOut ? 'items-end' : 'items-start'}`}>
                              {agentName && (
                                <span className="text-[11px] text-gray-400 mb-0.5 px-1">{agentName}</span>
                              )}
                              <div
                                className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed break-words whitespace-pre-wrap ${
                                  isOut
                                    ? 'bg-primary text-white rounded-br-md'
                                    : 'bg-slate-100 text-gray-900 rounded-bl-md'
                                } ${item._isOptimistic ? 'opacity-70' : ''}`}
                              >
                                {item.body}
                              </div>
                              <span className="text-[10px] text-gray-400 mt-0.5 px-1">
                                {formatMessageTime(item.created_at)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={threadEndRef} />
                    </div>
                  )}
                </div>

                {/* ── Input bar ── */}
                <div className="px-4 md:px-6 py-3 border-t border-gray-200 bg-white shrink-0">
                  <div className="flex items-end gap-2 max-w-2xl mx-auto">
                    <div className="flex-1 relative">
                      <textarea
                        ref={textareaRef}
                        value={newMessage}
                        onChange={handleTextareaInput}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message..."
                        rows={1}
                        className="w-full resize-none border border-gray-200 rounded-xl px-4 py-2.5 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-gray-400 bg-gray-50"
                        style={{ maxHeight: 96 }}
                      />
                      <span
                        className={`absolute right-3 bottom-2 text-[10px] ${
                          newMessage.length > SMS_CHAR_LIMIT ? 'text-red-500 font-medium' : 'text-gray-400'
                        }`}
                      >
                        {newMessage.length}/{SMS_CHAR_LIMIT}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={!newMessage.trim() || sendingMessage}
                      className="p-2.5 rounded-xl bg-primary text-white hover:bg-primary-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                    >
                      {sendingMessage
                        ? <Spinner size="sm" className="text-white" />
                        : <SafeIcon icon={FiSend} className="h-5 w-5" />
                      }
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
